import React from 'react';
import {
  arrayOf, bool, exact, func, string, number,
} from 'prop-types';
import { Droppable, DragDropContext } from 'react-beautiful-dnd';
import {
  Input, Flex, Typography, Checkbox,
} from '@atoms';
import { EditWarning } from '@molecules';
import { DraggableItems, ValueProcessModal } from '@organisms';
import { Edit } from '@templates';

const OptionProcess = ({
  onValueDelete,
  IsValueDeleteModalOpen,
  onValueDeleteModalChangeState,
  onValueEditSubmit,
  isValueEditing,
  isDataTheSame,
  onValueImageChange,
  onValueNameChange,
  onValuePreselectedStateChange,
  onValuePriceChange,
  valueName,
  isValuePreselected,
  valuePrice,
  valueImage,
  onValueModalClose,
  isValueModalOpen,
  error,
  minimalValuesAmount,
  maximalValuesAmount,
  onMaximalValuesAmountChange,
  onMinimalValuesAmountChange,
  isWarningOpen,
  onWarningChangeState,
  onDragSizeEnd,
  onOptionDelete,
  isModalOpen,
  onModalChangeState,
  values,
  isLoading,
  optionName,
  onToastClose,
  onOptionNameChange,
  onEditSubmit,
  onEditCancel,
  hasServerError,
  onOptionValueAdd,
  onOptionValueEdit,
  isEditing,
  isConfirmButtonDisabled,
  onOptionMultipleSelectionChange,
  isMultipleSelectionAllowed,
  onRequiredStateChange,
  isRequired,
}) => (
  <>
    <Edit
      error={error}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      onDelete={onOptionDelete}
      isModalOpen={isModalOpen}
      name={optionName}
      onModalChangeState={onModalChangeState}
      isEditing={isEditing}
      isLoading={isLoading}
      onToastClose={onToastClose}
      onEditCancel={onEditCancel}
      onEditSubmit={onEditSubmit}
      hasServerError={hasServerError}
    >
      <Input
        marginTop="xl2"
        type="text"
        id="optionName"
        name="optionName"
        placeholder="Option name"
        value={optionName}
        onChange={onOptionNameChange}
        data-cy="optionName"
      />
      <Flex itemsCenter selfStart marginTop="xl5">
        <Checkbox onCheckedStateChange={onOptionMultipleSelectionChange} isChecked={isMultipleSelectionAllowed} />
        <Typography marginLeft="lg" size="xl" weight="black">
          Is multiple selection allowed?
        </Typography>
      </Flex>
      <Flex itemsCenter selfStart marginTop="xl5">
        <Checkbox onCheckedStateChange={onRequiredStateChange} isChecked={isRequired} />
        <Typography marginLeft="lg" size="xl" weight="black">
          Is option required?
        </Typography>
      </Flex>
      <Typography marginTop="xl5" size="xl" weight="black">
        Minimum amount of required values
      </Typography>
      <Input
        marginTop="xl"
        type="text"
        id="minRequiredValuesAmount"
        name="minRequiredValuesAmount"
        value={minimalValuesAmount}
        onChange={onMinimalValuesAmountChange}
        placeholder="1"
        pattern="[0-9]*"
        data-cy="minRequiredValuesAmount"
      />
      <Typography marginTop="xl3" size="xl" weight="black">
        Maximum amount of required values
      </Typography>
      <Input
        marginTop="xl"
        type="text"
        id="maxRequiredValuesAmount"
        name="maxRequiredValuesAmount"
        value={maximalValuesAmount}
        onChange={onMaximalValuesAmountChange}
        placeholder="1"
        pattern="[0-9]*"
        data-cy="maxRequiredValuesAmount"
      />
      <Flex itemsCenter column paddingTop="xl5">
        <Typography marginBottom="md" size="xl3" weight="black">
          Values of current option
        </Typography>
        {!values.length && (
          <Flex justifyStart>
            <Typography size="lg" color="warmGrey" weight="black" marginBottom="lg">
              Nothing here yet
            </Typography>
          </Flex>
        )}
        <DragDropContext onDragEnd={onDragSizeEnd}>
          <Droppable droppableId="values">
            {provided => (
              <Flex column ref={provided.innerRef} {...provided.droppableProps}>
                <DraggableItems
                  isDataTheSame={isDataTheSame}
                  isConfirmButtonDisabled={isConfirmButtonDisabled}
                  onItemClick={onOptionValueEdit}
                  canBeAdded={isEditing}
                  onItemAdd={onOptionValueAdd}
                  items={values}
                />
                {provided.placeholder}
              </Flex>
            )}
          </Droppable>
        </DragDropContext>
      </Flex>
      <EditWarning isWarningOpen={isWarningOpen} onWarningClose={onWarningChangeState} />
      <ValueProcessModal
        onValueDelete={onValueDelete}
        IsValueDeleteModalOpen={IsValueDeleteModalOpen}
        onValueDeleteModalChangeState={onValueDeleteModalChangeState}
        onValueEditSubmit={onValueEditSubmit}
        isEditing={isValueEditing}
        valueName={valueName}
        valueImage={valueImage}
        valuePrice={valuePrice}
        isValuePreselected={isValuePreselected}
        onValueImageChange={onValueImageChange}
        onValueNameChange={onValueNameChange}
        onValuePriceChange={onValuePriceChange}
        onModalClose={onValueModalClose}
        isModalOpen={isValueModalOpen}
        onValuePreselectedStateChange={onValuePreselectedStateChange}
      />
    </Edit>
  </>
);

OptionProcess.propTypes = {
  onValueDelete: func.isRequired,
  IsValueDeleteModalOpen: bool.isRequired,
  onValueDeleteModalChangeState: func.isRequired,
  onValueEditSubmit: func.isRequired,
  isValueEditing: bool.isRequired,
  isDataTheSame: bool.isRequired,
  onValueImageChange: func.isRequired,
  onValueNameChange: func.isRequired,
  onValuePreselectedStateChange: func.isRequired,
  onValuePriceChange: func.isRequired,
  valueName: string.isRequired,
  isValuePreselected: bool.isRequired,
  valuePrice: string.isRequired,
  valueImage: string.isRequired,
  onValueModalClose: func.isRequired,
  isValueModalOpen: bool.isRequired,
  error: string.isRequired,
  minimalValuesAmount: number.isRequired,
  maximalValuesAmount: number.isRequired,
  onMaximalValuesAmountChange: func.isRequired,
  onMinimalValuesAmountChange: func.isRequired,
  onRequiredStateChange: func.isRequired,
  isRequired: bool.isRequired,
  onOptionMultipleSelectionChange: func.isRequired,
  isMultipleSelectionAllowed: bool.isRequired,
  isWarningOpen: bool.isRequired,
  onWarningChangeState: func.isRequired,
  onOptionValueEdit: func.isRequired,
  isConfirmButtonDisabled: bool.isRequired,
  onDragSizeEnd: func.isRequired,
  onOptionDelete: func.isRequired,
  isModalOpen: bool.isRequired,
  isEditing: bool.isRequired,
  onModalChangeState: func.isRequired,
  values: arrayOf(exact({
    id: number,
    name: string,
  })).isRequired,
  onOptionValueAdd: func.isRequired,
  onToastClose: func.isRequired,
  hasServerError: bool.isRequired,
  isLoading: bool.isRequired,
  optionName: string.isRequired,
  onOptionNameChange: func.isRequired,
  onEditSubmit: func.isRequired,
  onEditCancel: func.isRequired,
};

export default OptionProcess;
